import type { MouseEvent } from 'react';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as baseDealActionCreators from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import * as baseDealSelectors from '@makemydeal/dr-dash-store/selectors/BaseDealSelectors';
import { scopedStateSelectors } from '@makemydeal/shared-scoped-store';
import { BreakdownTabsName, BreakdownTabsLabel } from '@makemydeal/dr-shared-types';
import { ToggleGroup } from '@interstate/components/ToggleGroup';
import { Box } from '@interstate/components/Box';
import InterstateDealSummary from '../../../../dealSummary/DealSummary.interstate';
import DealRecapSummary from '../../../../dealSummary/DealRecap/DealRecapSummary.interstate';

interface BreakdownTabsProps {
    scopeId?: string;
}

const BreakdownTabs: React.FC<BreakdownTabsProps> = ({ scopeId = '' }) => {
    const dispatch = useDispatch();
    const primaryScopeId = useSelector(scopedStateSelectors.getPrimaryScopeId);
    const activeBreakdownTab = useSelector(baseDealSelectors.getActiveBreakdownTabDetails);
    const targetScopeId = scopeId || primaryScopeId;
    const activeTab = activeBreakdownTab?.[targetScopeId] ?? BreakdownTabsName.BREAKDOWN;

    const handleTabToggle = (_e: MouseEvent, value: BreakdownTabsName) => {
        if (targetScopeId) {
            dispatch(baseDealActionCreators.setActiveBreakdownTabDetails({ [targetScopeId]: value }));
        }
    };

    const isBreakdownActive = activeTab === BreakdownTabsName.BREAKDOWN;
    const isDealRecapActive = activeTab === BreakdownTabsName.DEALRECAP;

    return (
        <>
            <Box px={1}>
                <ToggleGroup
                    size="sm"
                    toggleWidth="100%"
                    data-testid="breakdown-tabs"
                    onChange={handleTabToggle}
                    toggleButtonOptions={[
                        {
                            selected: isBreakdownActive,
                            'data-testid': 'breakdown-tab',
                            'aria-label': BreakdownTabsLabel.BREAKDOWN,
                            children: BreakdownTabsLabel.BREAKDOWN,
                            value: BreakdownTabsName.BREAKDOWN
                        },
                        {
                            selected: isDealRecapActive,
                            'data-testid': 'deal-recap-tab',
                            'aria-label': BreakdownTabsLabel.DEALRECAP,
                            children: BreakdownTabsLabel.DEALRECAP,
                            value: BreakdownTabsName.DEALRECAP
                        }
                    ]}
                />
            </Box>
            <Box display={isBreakdownActive ? 'block' : 'none'}>
                <InterstateDealSummary scopeId={targetScopeId} />
            </Box>
            <Box display={isDealRecapActive ? 'block' : 'none'}>
                <DealRecapSummary scopeId={targetScopeId} />
            </Box>
        </>
    );
};

export default BreakdownTabs;
